import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaHome, FaRedo } from 'react-icons/fa';

/**
 * Payment status configuration map.
 * Add new statuses here (e.g. 'pending', 'cancelled', 'refunded') to extend.
 */
const STATUS_CONFIG = {
  success: {
    icon: FaCheckCircle,
    iconColor: '#28a745',
    bgGradient: 'linear-gradient(135deg, #d4edda 0%, #f0faf3 100%)',
    accentColor: '#28a745',
    heading: 'Payment Successful',
    description: 'Your payment has been completed successfully.\nYour booking has been confirmed.',
    showTryAgain: false,
  },
  failure: {
    icon: FaTimesCircle,
    iconColor: '#dc3545',
    bgGradient: 'linear-gradient(135deg, #f8d7da 0%, #fdf0f0 100%)',
    accentColor: '#dc3545',
    heading: 'Payment Failed',
    description: 'Unfortunately your payment could not be completed.\nPlease try again.',
    showTryAgain: true,
  },
};

const PaymentStatus = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const txnid = new URLSearchParams(location.search).get('txnid') || '';

  const config = STATUS_CONFIG[type];

  // Invalid payment status
  if (!config) {
    return (
      <div className="payment-status-wrapper">
        <div className="payment-status-card">
          <div className="payment-status-icon-ring" style={{ background: 'linear-gradient(135deg, #fff3cd 0%, #fffdf5 100%)' }}>
            <FaExclamationTriangle size={48} color="#ffc107" />
          </div>
          <h2 className="payment-status-heading">Invalid Payment Status</h2>
          <p className="payment-status-desc">
            The payment status <strong>"{type}"</strong> is not recognized.
          </p>
          <div className="payment-status-actions">
            <button className="btn payment-status-btn-primary" onClick={() => navigate('/')}>
              <FaHome className="me-2" />Go to Home
            </button>
          </div>
        </div>

        <PaymentStatusStyles />
      </div>
    );
  }

  const IconComponent = config.icon;

  return (
    <div className="payment-status-wrapper">
      <div className="payment-status-card">
        <div className="payment-status-icon-ring" style={{ background: config.bgGradient }}>
          <IconComponent size={48} color={config.iconColor} />
        </div>

        <h2 className="payment-status-heading">{config.heading}</h2>

        <p className="payment-status-desc">
          {config.description.split('\n').map((line, i) => (
            <span key={i}>
              {line}
              {i < config.description.split('\n').length - 1 && <br />}
            </span>
          ))}
        </p>

        {txnid && (
          <div className="payment-status-txnid">
            <span className="payment-status-txnid-label">Transaction ID</span>
            <span className="payment-status-txnid-value">{txnid}</span>
          </div>
        )}

        <div className="payment-status-actions">
          {config.showTryAgain && (
            <button
              className="btn payment-status-btn-outline"
              style={{ borderColor: config.accentColor, color: config.accentColor }}
            // onClick={() => navigate(-1)}
            >
              <FaRedo className="me-2" />Close this page
            </button>
          )}
          <button className="btn payment-status-btn-primary" onClick={() => navigate('/')}>
            <FaHome className="me-2" />Go to Home
          </button>
        </div>
      </div>

      <PaymentStatusStyles />
    </div>
  );
};

/** Scoped styles for the payment status page */
const PaymentStatusStyles = () => (
  <style>{`
    .payment-status-wrapper {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.5rem;
      background: linear-gradient(160deg, #f8f9fa 0%, #e9ecef 100%);
    }

    .payment-status-card {
      width: 100%;
      max-width: 480px;
      background: #ffffff;
      border-radius: 20px;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
      padding: 3rem 2.5rem;
      text-align: center;
      animation: paymentCardIn 0.5s ease-out;
    }

    @keyframes paymentCardIn {
      from {
        opacity: 0;
        transform: translateY(24px) scale(0.97);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .payment-status-icon-ring {
      width: 96px;
      height: 96px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.75rem;
      animation: iconPulse 2s ease-in-out infinite;
    }

    @keyframes iconPulse {
      0%, 100% { transform: scale(1); }
      50%      { transform: scale(1.05); }
    }

    .payment-status-heading {
      font-size: 1.6rem;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 0.75rem;
    }

    .payment-status-desc {
      font-size: 1rem;
      line-height: 1.7;
      color: #6c757d;
      margin-bottom: 2rem;
    }

    .payment-status-txnid {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25rem;
      margin-bottom: 2rem;
      padding: 0.75rem 1.25rem;
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 10px;
    }

    .payment-status-txnid-label {
      font-size: 0.8rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .payment-status-txnid-value {
      font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
      font-size: 0.9rem;
      font-weight: 500;
      color: #1a1a1a;
      word-break: break-all;
      user-select: all;
      cursor: text;
    }

    .payment-status-actions {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    .payment-status-btn-primary {
      background: linear-gradient(135deg, #006bff, #0056cc) !important;
      color: #ffffff !important;
      font-weight: 600 !important;
      border: none !important;
      border-radius: 10px !important;
      padding: 0.65rem 1.5rem !important;
      font-size: 0.95rem !important;
      display: inline-flex !important;
      align-items: center !important;
      transition: all 0.2s ease !important;
    }

    .payment-status-btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 107, 255, 0.35) !important;
    }

    .payment-status-btn-outline {
      background: transparent !important;
      font-weight: 600 !important;
      border: 2px solid !important;
      border-radius: 10px !important;
      padding: 0.65rem 1.5rem !important;
      font-size: 0.95rem !important;
      display: inline-flex !important;
      align-items: center !important;
      transition: all 0.2s ease !important;
    }

    .payment-status-btn-outline:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1) !important;
    }

    @media (max-width: 480px) {
      .payment-status-card {
        padding: 2rem 1.5rem;
        border-radius: 16px;
      }

      .payment-status-heading {
        font-size: 1.35rem;
      }

      .payment-status-icon-ring {
        width: 80px;
        height: 80px;
      }

      .payment-status-icon-ring svg {
        width: 40px !important;
        height: 40px !important;
      }

      .payment-status-actions {
        flex-direction: column;
      }

      .payment-status-btn-primary,
      .payment-status-btn-outline {
        width: 100% !important;
        justify-content: center !important;
      }
    }
  `}</style>
);

export default PaymentStatus;
